/**
 * Whether a page's job is search traffic, decided from where the site puts it.
 *
 * Delete and merge advice is search advice: a page with no search traffic and no cluster may be dead
 * weight, and two pages found for one search may be one page too many. Neither holds for a page whose
 * job is something else. A customer story is there to convince a buyer, a legal page because the law
 * says so, an about or pricing page because the menu needs one; none of them is expected to earn
 * clicks, and "no clicks" says nothing about them (quality sprint 5, workstream G).
 *
 * The role comes from the site's own structure, never from words in the address:
 *
 * - **Navigation page.** The site links the page from at least `PAGE_ROLE_NAVIGATION_MIN_SHARE` of its
 *   pages, which only a site-wide menu or footer does (about, pricing, legal, section indexes, a
 *   featured page). Removing or merging it is a change to the site's navigation, not a search fix.
 * - **Showcase section.** The page sits in a section whose index the navigation links (as above), and
 *   whose pages are each about another organisation: for at least
 *   `PAGE_ROLE_SHOWCASE_MIN_OTHER_SITE_SHARE` of the section's pages (and at least
 *   `PAGE_ROLE_SHOWCASE_MIN_PAGES` of them), the search the page is compared on is that organisation's
 *   own name, and Google's top result is that organisation's own site. That is the shape of a
 *   customer-story or case-study section. A section of comparison or integration pages has the second
 *   half of the shape but not the first: the site does not list it in its navigation, it exists to be
 *   found, and its delete advice stands.
 *
 * - **Translation of such a page** (quality sprint 7 D). A page that names a navigation or showcase
 *   page as its `hreflang` alternate, or is named as one by it, takes that page's role
 *   (`carryPageRolesAcrossLanguages`): a translated section carries its own menu, linked from too few
 *   of the site's pages to clear the navigation share, so its privacy page or booking form would
 *   otherwise read as a search page. The link is the site's own declaration, never the words.
 *
 * Everything else is a search page and keeps the advice it had.
 *
 * Measured on the quality sprint 5 frozen data (both sites, every tracked page; `page-role-evidence.ts`):
 * - Navigation share. ManyRequests (408 pages with links): menu and footer pages at 0.99, a featured
 *   page at 0.92, two more at 0.58 and 0.51; the most-linked page below the cut is a blog post at 0.47
 *   (a block on the blog template). SlideSpeak (797): menu and footer pages at 1.00 and 0.72 (the
 *   translated sections carry their own menu), then nothing above 0.37. The cut at 0.5 has a margin of
 *   0.01 above and 0.03 below on ManyRequests (a section index linked from half the site's menu; re-measure
 *   when the menu changes), 0.22 and 0.13 on SlideSpeak.
 * - Other-organisation share, sections whose index is in the navigation: ManyRequests' customer stories
 *   12 of 15 (0.80); every other such section on either site 0.04 or less (guides 1 of 25, prompt library
 *   4 of 128, blog 2 of 240). Sections not in the navigation reach 12 of 16 (comparisons) and 15 of 21
 *   (integrations), which is why the navigation half is required.
 */

/** A site-wide menu or footer link: the share of the site's pages that link to this page. */
export const PAGE_ROLE_NAVIGATION_MIN_SHARE = 0.5;

/** A showcase section: the share of its pages whose search is another organisation's own name. */
export const PAGE_ROLE_SHOWCASE_MIN_OTHER_SITE_SHARE = 0.5;

/** A showcase section needs this many pages with a stored search before its share means anything. */
export const PAGE_ROLE_SHOWCASE_MIN_PAGES = 3;

export type PageRole =
  | { kind: 'search' }
  | {
      kind: 'navigation';
      /** Share of the site's pages that link to it, 0–1. */
      navigationShare: number;
      /** Set when the role is carried from this page's language alternate: that page's path. */
      translationOf?: string;
    }
  | {
      kind: 'showcase';
      /** The section's pages with a stored search (its index page aside). */
      sectionPagesWithSearch: number;
      /** Of those, the pages whose search is another organisation's name, answered by its own site. */
      sectionPagesAboutAnotherSite: number;
      /** Set when the role is carried from this page's language alternate: that page's path. */
      translationOf?: string;
    };

export interface PageRoleInput {
  urlId: string;
  /** The page's path, as `pageRolePath` normalises it. */
  path: string;
  /** Paths this page links to (`pageRolePath`), or null when the page has no stored links. */
  linksTo: string[] | null;
  /**
   * The search the page is compared on is another organisation's name, and Google's top result for it
   * is that organisation's own site. Null when the page has no stored search.
   */
  searchNamesAnotherSite: boolean | null;
}

/** Path only, lower case, no trailing slash: `/Customer-Stories/x/?a=1` → `/customer-stories/x`. */
export function pageRolePath(urlOrPath: string): string {
  let path = String(urlOrPath || '').trim();
  try {
    path = new URL(path, 'https://site.invalid').pathname;
  } catch {
    path = path.split(/[?#]/)[0];
  }
  path = path.toLowerCase().replace(/\/+$/, '');
  return path || '/';
}

/** The section index a page sits under (`/a/b` → `/a`), or null for a top-level page. */
function sectionIndexOf(path: string): string | null {
  const segments = path.split('/').filter(Boolean);
  return segments.length > 1 ? `/${segments[0]}` : null;
}

/** Every page's role, from the site's link graph and stored searches. Pages default to search. */
export function buildPageRoles(pages: PageRoleInput[]): Map<string, PageRole> {
  const linking = pages.filter((p) => Array.isArray(p.linksTo) && p.linksTo.length > 0);
  const inbound = new Map<string, number>();
  for (const p of linking) {
    for (const target of new Set(p.linksTo)) {
      if (target === p.path) continue;
      inbound.set(target, (inbound.get(target) ?? 0) + 1);
    }
  }
  const navigationShare = (path: string): number =>
    linking.length ? (inbound.get(path) ?? 0) / linking.length : 0;

  const sections = new Map<string, { withSearch: number; aboutAnotherSite: number }>();
  for (const p of pages) {
    const section = sectionIndexOf(p.path);
    if (!section || p.searchNamesAnotherSite === null) continue;
    const s = sections.get(section) ?? { withSearch: 0, aboutAnotherSite: 0 };
    s.withSearch += 1;
    if (p.searchNamesAnotherSite) s.aboutAnotherSite += 1;
    sections.set(section, s);
  }

  const roles = new Map<string, PageRole>();
  for (const p of pages) {
    const share = navigationShare(p.path);
    if (share >= PAGE_ROLE_NAVIGATION_MIN_SHARE) {
      roles.set(p.urlId, { kind: 'navigation', navigationShare: share });
      continue;
    }
    const section = sectionIndexOf(p.path);
    const s = section ? sections.get(section) : undefined;
    if (
      section &&
      s &&
      navigationShare(section) >= PAGE_ROLE_NAVIGATION_MIN_SHARE &&
      s.withSearch >= PAGE_ROLE_SHOWCASE_MIN_PAGES &&
      s.aboutAnotherSite / s.withSearch >= PAGE_ROLE_SHOWCASE_MIN_OTHER_SITE_SHARE
    ) {
      roles.set(p.urlId, {
        kind: 'showcase',
        sectionPagesWithSearch: s.withSearch,
        sectionPagesAboutAnotherSite: s.aboutAnotherSite
      });
      continue;
    }
    roles.set(p.urlId, { kind: 'search' });
  }
  return roles;
}

/** One page's language alternates, as the site declares them (`hreflang`), by page id. */
export interface PageRoleAlternates {
  urlId: string;
  /** Ids of the pages this page names as its `hreflang` alternates (itself excluded). */
  alternateIds: readonly string[];
}

/**
 * A search page that is the translation of a navigation or showcase page takes that page's role,
 * with `translationOf` set to its path. The tie between the two is the `hreflang` declaration in
 * either direction. Only roles decided from structure are carried (never a carried one again), and
 * when several alternates have one the most-linked navigation page wins, then the path.
 */
export function carryPageRolesAcrossLanguages(
  roles: ReadonlyMap<string, PageRole>,
  alternates: readonly PageRoleAlternates[],
  pathById: ReadonlyMap<string, string>
): Map<string, PageRole> {
  const linked = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (a === b) return;
    const s = linked.get(a) ?? new Set<string>();
    s.add(b);
    linked.set(a, s);
  };
  for (const a of alternates) {
    for (const b of a.alternateIds) {
      link(a.urlId, b);
      link(b, a.urlId);
    }
  }
  const out = new Map(roles);
  for (const [id, others] of linked) {
    if (pageRoleIsNotSearch(roles.get(id))) continue;
    const sources = [...others]
      .map((o) => ({ id: o, role: roles.get(o), path: pathById.get(o) ?? '' }))
      .filter((o): o is { id: string; role: Exclude<PageRole, { kind: 'search' }>; path: string } =>
        pageRoleIsNotSearch(o.role)
      )
      .sort(
        (x, y) =>
          (y.role.kind === 'navigation' ? y.role.navigationShare : 0) -
            (x.role.kind === 'navigation' ? x.role.navigationShare : 0) ||
          (x.path < y.path ? -1 : x.path > y.path ? 1 : 0)
      );
    const src = sources[0];
    if (src) out.set(id, { ...src.role, translationOf: src.path });
  }
  return out;
}

/** The page's job is something other than search traffic. A missing role is a search page. */
export function pageRoleIsNotSearch(role: PageRole | null | undefined): boolean {
  return Boolean(role && role.kind !== 'search');
}

/** Why the page is not judged on search traffic, in one sentence a client can read (null for search). */
export function pageRoleSentence(role: PageRole | null | undefined): string | null {
  if (!role || role.kind === 'search') return null;
  if (role.translationOf && role.kind === 'navigation') {
    const pct = Math.round(role.navigationShare * 100);
    return (
      `This page is another language's version of ${role.translationOf}, which the site links from its menu ` +
      `or footer (on ${pct}% of its pages): part of how visitors find their way around, so having no search ` +
      'traffic is not a reason to remove it.'
    );
  }
  if (role.translationOf) {
    return (
      `This page is another language's version of ${role.translationOf}, in a section the site links from its ` +
      'menu whose pages are each about another organisation, like customer stories. Pages like these are there ' +
      'for visitors, not for search traffic.'
    );
  }
  if (role.kind === 'navigation') {
    const pct = Math.round(role.navigationShare * 100);
    return (
      `The site links this page from its menu or footer (on ${pct}% of its pages), so it is part of how ` +
      'visitors find their way around, and having no search traffic is not a reason to remove it.'
    );
  }
  return (
    'This page is in a section the site links from its menu, whose pages are each about another ' +
    `organisation (for ${role.sectionPagesAboutAnotherSite} of ${role.sectionPagesWithSearch}, Google's top ` +
    "result for the page's search is that organisation's own site), like customer stories. Pages like " +
    'these are there for visitors, not for search traffic.'
  );
}
